import classNames from "classnames"
import { format, parseISO } from "date-fns"
import utcToZonedTime from "date-fns-tz/utcToZonedTime"
import React from "react"

import Badge from "src/components/Badge"
import BoatPreviewLink from "src/components/BoatPreviewLink"
import CellSelect from "src/components/DataTableV2/CellSelect"
import TextCollapsible from "src/components/TextCollapsible"
import Tooltip from "src/components/Tooltip"

import { titlecase } from "src/utils/string_helpers"

import { timeCellContent } from "./MarinaScheduleSharedMethods"

const BOATER_STATUS_OPTIONS = {
  scheduled: [{ value: "arrived", label: "Boater arrived", color: "blue" }],
  launched: [
    { value: "ready_for_return", label: "Ready for return", color: "teal" },
    {
      value: "not_ready_for_return",
      label: "Do not return",
      color: "yellow",
    },
  ],
}

const getBoaterStatusLabel = (status, boaterStatus) => {
  const options = BOATER_STATUS_OPTIONS[status] || []
  const option = options.find((opt) => opt.value === boaterStatus)
  if (!option) return null
  return <Badge text={option.label} color={option.color} variant="small" />
}

const contactColumn = ({ marinaSlug }) => ({
  key: "contact",
  header: "Customer",
  cardRole: "title",
  render: (row) => {
    const boat = row.contactBoat

    return (
      <div className="overflow-hidden">
        <div className="truncate">{row.contact.name}</div>
        <div className="text-muted truncate text-xs">
          <BoatPreviewLink boat={boat} />
        </div>
      </div>
    )
  },
  cardRender: (row) => {
    const boat = row.contactBoat

    return <BoatPreviewLink boat={boat} />
  },
  width: "180px",
})

const balanceColumn = () => ({
  key: "balance",
  header: "",
  cardRole: "subtitle",
  render: (row) =>
    row.contact?.hasOutstandingBalance ? (
      <Tooltip text="This boater has an outstanding balance" variant="light">
        <i className="fa-regular fa-triangle-exclamation text-yellow-700" />
      </Tooltip>
    ) : null,
  cardRender: (row) => (
    <div className="flex items-center gap-2">
      <span>{row.contact.name}</span>
      {row.contact?.hasOutstandingBalance && (
        <Tooltip text="This boater has an outstanding balance" variant="light">
          <i className="fa-regular fa-triangle-exclamation text-yellow-700" />
        </Tooltip>
      )}
    </div>
  ),
  width: "20px",
})

const spaceNameColumn = () => ({
  key: "spaceName",
  header: "From",
  cardRole: "detail",
  render: (row) => row.spaceName || "-",
  width: "150px",
})

const locationColumn = () => ({
  key: "location",
  header: "To",
  cardRole: "detail",
  render: (row) => (row.location ? titlecase(row.location) : "-"),
  width: "150px",
})

const boaterStatusColumn = ({ onUpdateBoaterStatus, canManage }) => ({
  key: "boaterStatus",
  header: "Boater Status",
  cardRole: "badge",
  render: (row) => {
    const options = BOATER_STATUS_OPTIONS[row.status] || []
    return (
      <CellSelect
        value={row.boaterStatus}
        label={getBoaterStatusLabel(row.status, row.boaterStatus)}
        placeholder="Set status"
        onChange={(value) => onUpdateBoaterStatus(row.encodedId, value)}
        disabled={!canManage}
        clearLabel="Clear status"
      >
        {options.map((option) => (
          <CellSelect.Option key={option.value} value={option.value}>
            <Badge text={option.label} color={option.color} variant="small" />
          </CellSelect.Option>
        ))}
      </CellSelect>
    )
  },
})

const estimatedReturnColumn = () => ({
  key: "estimatedReturnTime",
  header: "Est. Return",
  cardRole: "detail",
  render: (row) => {
    if (!row.estimatedReturnTime) return "-"
    const zonedTime = utcToZonedTime(
      parseISO(row.estimatedReturnTime),
      row.marinaTimezone
    )
    return (
      <div>
        <div>{format(parseISO(row.estimatedReturnTime), "MM/dd/yy")}</div>
        <div className="text-muted text-xs">{format(zonedTime, "p")}</div>
      </div>
    )
  },
  cardRender: (row) => {
    if (!row.estimatedReturnTime) return null
    const zonedTime = utcToZonedTime(
      parseISO(row.estimatedReturnTime),
      row.marinaTimezone
    )
    return (
      <span className="text-muted">
        Est. Return: {format(zonedTime, "MMMM dd, yyyy 'at' p")}
      </span>
    )
  },
})

const launchNoteColumn = () => ({
  key: "launchNote",
  header: "Launch Note",
  cardRole: "footer",
  render: (row) => {
    if (!row.note) return "-"
    return (
      <TextCollapsible lines={2} className="py-2">
        <span>{row.note}</span>
      </TextCollapsible>
    )
  },
  cardRender: (row) => {
    if (!row.note) return null
    return (
      <TextCollapsible lines={2}>
        <p className="mb-0 font-semibold">Launch Note:</p>
        <span>{row.note}</span>
      </TextCollapsible>
    )
  },
  width: "300px",
})

const reservationNoteColumn = () => ({
  key: "reservationNote",
  header: "Contract/Res. Note",
  cardRole: "footer",
  render: (row) => {
    if (!row.reservationNote) return "-"
    return (
      <TextCollapsible lines={2} className="py-2">
        <span>{row.reservationNote}</span>
      </TextCollapsible>
    )
  },
  cardRender: (row) => {
    if (!row.reservationNote) return null
    const label = row.belongsToContract ? "Contract Note:" : "Reservation Note:"
    return (
      <TextCollapsible lines={2}>
        <p className="mb-0 font-semibold">{label}</p>
        <span>{row.reservationNote}</span>
      </TextCollapsible>
    )
  },
  width: "300px",
})

// LaunchAddonSerializer sends the raw numeric(11,5) quantity column, which
// as_json's to a trailing-zero string ("2.00000"). The chip below is its own
// compact format — not the full sentence DryStack::LaunchAddonDecorator#detail
// renders — so it trims the string itself rather than borrowing that field.
const trimmedQuantity = (quantity) => String(Number(quantity))

const addonChipSuffix = (addon) => {
  if (addon.fuelMode === "fill_up") return "full tank"
  if (addon.fuelMode === "gallons")
    return `${trimmedQuantity(addon.quantity)} ${addon.fuelUnitSymbol}`
  return `×${trimmedQuantity(addon.quantity)}`
}

// Figma's add-on chips are teal-outline Badges — Badge carries the color in its
// enum and the design-system marker, so no arbitrary hex or one-off chip. The
// hover popover shows the full list, so a row with more chips than the two-line
// cell can hold isn't a dead end. Badge takes no className, so the per-chip
// spacing the clamped cell needs (line-clamp can't use flex gap) rides on a
// wrapper span.
const renderAddOnChip = (addon, className) => (
  <span key={addon.id} className={classNames("inline-block", className)}>
    <Badge
      color={addon.fuelMode ? "teal-outline" : "gray"}
      variant="small"
      text={`${addon.name} ${addonChipSuffix(addon)}`}
    />
  </span>
)

const renderAddOnChipRow = (addons) => (
  <div className="flex flex-wrap items-center gap-1">
    {addons.map((addon) => renderAddOnChip(addon))}
  </div>
)

const renderAddOnHoverContent = (addons) => (
  <div className="flex w-full flex-col gap-3">
    <p className="text-sm font-semibold text-gray-900">Add-Ons</p>
    <div className="h-px w-full bg-gray-200" />
    {renderAddOnChipRow(addons)}
  </div>
)

const launchAddOnsColumn = () => ({
  key: "launchAddOns",
  header: "Add-Ons",
  cardRole: "footer",
  render: (row) => {
    if (!row.addons || row.addons.length === 0) return "-"
    return (
      <Tooltip
        variant="light"
        maxWidth="320px"
        text={renderAddOnHoverContent(row.addons)}
      >
        <div className="line-clamp-2 py-2 leading-6">
          {row.addons.map((addon) => renderAddOnChip(addon, "mb-1 mr-1"))}
        </div>
      </Tooltip>
    )
  },
  cardRender: (row) => {
    // Like the table cell, the card shows the add-on chips only — same content,
    // not the same layout (the card wraps freely; the table cell clamps to two
    // lines). An operator working a launch needs to know what to put in the
    // boat, not the Subtotal/Tax/Estimated-total breakdown or the pricing
    // disclaimer — those stay on the boater-facing views (MOB-1105). DataTableV2's
    // CardView prefers cardRender over render wholesale, so this returns null
    // (not the render's dash) when there are no add-ons, letting the card skip
    // the slot entirely.
    if (!row.addons || row.addons.length === 0) return null
    return renderAddOnChipRow(row.addons)
  },
  width: "260px",
})

export const getScheduledColumns = ({
  viewingAll,
  unviewedIds,
  onUpdateBoaterStatus,
  canManage,
  marinaSlug,
}) => [
  {
    key: "unviewed",
    header: "",
    cardRole: "hidden",
    render: (row) =>
      unviewedIds.has(row.encodedId) ? (
        <div className="h-2 w-2 rounded-full bg-blue-600" />
      ) : (
        <div />
      ),
    width: "20px",
  },
  {
    key: "startTime",
    header: "Scheduled",
    cardRole: "title",
    render: (row) => timeCellContent(row, viewingAll),
    cardRender: (row) => (
      <div>
        {timeCellContent(row, viewingAll)}
        <div className="my-4 w-full border-b" />
      </div>
    ),
    width: "225px",
  },
  contactColumn({ marinaSlug }),
  balanceColumn(),
  spaceNameColumn(),
  locationColumn(),
  boaterStatusColumn({ onUpdateBoaterStatus, canManage }),
  launchAddOnsColumn(),
  estimatedReturnColumn(),
  launchNoteColumn(),
  reservationNoteColumn(),
]

export const getLaunchedColumns = ({
  onUpdateBoaterStatus,
  canManage,
  marinaSlug,
}) => [
  {
    key: "startTime",
    header: "Launched",
    cardRole: "title",
    render: (row) => timeCellContent(row, false),
    cardRender: (row) => (
      <div>
        {timeCellContent(row, false)}
        <div className="my-4 w-full border-b" />
      </div>
    ),
  },
  contactColumn({ marinaSlug }),
  balanceColumn(),
  spaceNameColumn(),
  locationColumn(),
  boaterStatusColumn({ onUpdateBoaterStatus, canManage }),
  launchAddOnsColumn(),
  estimatedReturnColumn(),
  launchNoteColumn(),
  reservationNoteColumn(),
]

export const getReturnedColumns = ({ marinaSlug }) => [
  {
    key: "startTime",
    header: "Returned",
    cardRole: "title",
    render: (row) => timeCellContent(row, false),
    cardRender: (row) => (
      <div>
        {timeCellContent(row, false)}
        <div className="my-4 w-full border-b" />
      </div>
    ),
  },
  contactColumn({ marinaSlug }),
  balanceColumn(),
  spaceNameColumn(),
  locationColumn(),
  launchAddOnsColumn(),
  launchNoteColumn(),
  reservationNoteColumn(),
]
